import { createSlice, current, type PayloadAction } from "@reduxjs/toolkit";
import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";
import unset from "lodash/unset";
import { emptyConfiguration } from "@/features/shops/lib";
import type { ShopConfiguration } from "@/shared/types/shop";

/** Percorso dentro la configurazione, per segmenti: i nomi degli attributi possono contenere punti. */
export type FieldPath = (string | number)[];
export interface FieldChange {
  path: FieldPath;
  value: unknown;
}

export interface ConfigurationState {
  currentConfiguration: ShopConfiguration;
  historyChanges: ShopConfiguration[];
  websiteAttributes: string[];
}

export const initialConfigurationState: ConfigurationState = {
  currentConfiguration: emptyConfiguration(),
  historyChanges: [],
  websiteAttributes: [],
};

/** Ogni modifica dell'utente spinge PRIMA uno snapshot (come le viste legacy con cloneDeep sulla history). */
function snapshot(state: ConfigurationState): void {
  state.historyChanges.push(cloneDeep(current(state.currentConfiguration)));
}

/**
 * Slice `configuration` della spec (5.3). Finche' le viste legacy leggono `currentConfiguration`/`historyChanges`/
 * `websiteAttributes` dallo slice `legacy`, questi case reducer vengono applicati su quei campi dagli
 * `extraReducers` di `legacySlice` (una sola verita'); il reducer di questo slice non e' montato su una propria
 * chiave finche' l'ultima vista legacy non sparisce (fase 8).
 */
export const configurationSlice = createSlice({
  name: "configuration",
  initialState: initialConfigurationState,
  reducers: {
    /** Carica la configurazione dello shop (clone) e azzera la history (applyShop / bootstrap). */
    startEditing(state, action: PayloadAction<ShopConfiguration>) {
      state.currentConfiguration = cloneDeep(action.payload);
      state.historyChanges = [];
    },
    /** Sostituisce la configurazione senza toccare la history (RenameShopModal legacy dopo la rilettura degli shop). */
    replaceConfiguration(state, action: PayloadAction<ShopConfiguration>) {
      state.currentConfiguration = cloneDeep(action.payload);
    },
    updateField(state, action: PayloadAction<FieldChange>) {
      snapshot(state);
      set(state.currentConfiguration, action.payload.path, action.payload.value);
    },
    /** Piu' percorsi, un solo snapshot (es. feedUrl in `setup` e in `cms_data`). */
    updateFields(state, action: PayloadAction<FieldChange[]>) {
      snapshot(state);
      for (const change of action.payload) set(state.currentConfiguration, change.path, change.value);
    },
    /** `delete` del legacy: la chiave sparisce (una chiave `undefined` conterebbe come differenza in isEqual). */
    unsetField(state, action: PayloadAction<{ path: FieldPath }>) {
      snapshot(state);
      unset(state.currentConfiguration, action.payload.path);
    },
    /** Scrive senza snapshot: dove il legacy scriveva il campo senza spingere la history. */
    patchField(state, action: PayloadAction<FieldChange>) {
      set(state.currentConfiguration, action.payload.path, action.payload.value);
    },
    /** SaveChanges.discardChanges: torna all'ultimo snapshot e lo toglie; con history vuota non tocca nulla. */
    undo(state) {
      const previous = state.historyChanges.pop();
      if (previous) state.currentConfiguration = previous;
    },
    markSaved(state) {
      state.historyChanges = [];
    },
    setWebsiteAttributes(state, action: PayloadAction<string[]>) {
      state.websiteAttributes = action.payload;
    },
  },
});

export const {
  startEditing,
  replaceConfiguration,
  updateField,
  updateFields,
  unsetField,
  patchField,
  undo,
  markSaved,
  setWebsiteAttributes,
} = configurationSlice.actions;
export const configurationReducers = configurationSlice.caseReducers;
